//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import {
  GetYouTubeConnectionStatusResponse,
  GetYouTubeOAuthUrlResponse,
  IOAuthYouTubeRequest,
  OAuthYouTubeResponse,
  YouTubeConnectionStatus,
} from '@apis/youtube-settings/youtubeSettingsApiModel';
import { isDefined } from '@utils/filterPredicates';
import { makeParams } from '@utils/makeParams';
import { serializedGatewayFetch } from '@utils/sessionFetch';
import { ISerializedResponse } from '@utils/types/SerializedResponse';

const YOUTUBE_DEFAULT_ENDPOINT = '/api/v1/oauth';

const youtubeGatewayOAuthFetch = (
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> => {
  return serializedGatewayFetch(
    'youtube_gateway',
    `${YOUTUBE_DEFAULT_ENDPOINT}${endpoint}`,
    init,
  );
};

/**
 * Gets the YouTube token connection status
 *
 * GET /api/v1/oauth
 */
export const getYouTubeConnectionStatus = async (
  init?: RequestInit,
): Promise<YouTubeConnectionStatus> => {
  const res = await youtubeGatewayOAuthFetch(`/`, init);

  const json = await res.json<GetYouTubeConnectionStatusResponse>();

  return json.data;
};

/**
 * Gets url to oauth with YouTube
 *
 * GET /api/v1/oauth/url
 */
export const getYouTubeAuthorizationUrl = async (
  init?: RequestInit,
): Promise<string> => {
  const res = await youtubeGatewayOAuthFetch(`/url`, init);

  const json = await res.json<GetYouTubeOAuthUrlResponse>();

  return json.data;
};

/**
 * Connect the client's YouTube using the provided authCode
 *
 * POST /api/v1/oauth/
 */
export const connectYouTube = async (
  request: IOAuthYouTubeRequest,
  init?: RequestInit,
): Promise<YouTubeConnectionStatus> => {
  const queryParams = makeParams(request);
  const res = await youtubeGatewayOAuthFetch(`/?${queryParams}`, {
    ...(isDefined(init) ? init : {}),
    method: 'POST',
  });

  const json = await res.json<OAuthYouTubeResponse>();

  return json.data;
};

/**
 * Revoke the client's YouTube token
 *
 * DELETE /api/v1/oauth/
 */
export const disconnectYouTube = async (init?: RequestInit): Promise<void> => {
  await youtubeGatewayOAuthFetch(`/`, {
    ...(isDefined(init) ? init : {}),
    method: 'DELETE',
  });
};
